/**
 * Elevated privilege sessions — PRD §5.4F（业主审批链接；console_sim 仅非生产）
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  cloudApproveElevatedSession,
  cloudHealth,
  cloudListElevatedSessions,
  cloudListProjectElevatedSessions,
  cloudRequestElevatedSession,
  cloudResendElevatedNotify,
  cloudRevokeElevatedSession,
  ensureCloudSession,
  getCloudToken,
  type ApiElevatedNotify,
  type ApiElevatedSession,
} from "../api/client";
import { useAuth } from "../auth/AuthContext";

interface ElevatedState {
  cloudReady: boolean;
  active: ApiElevatedSession[];
  all: ApiElevatedSession[];
  syncing: boolean;
  allowConsoleSim: boolean;
  refresh: () => Promise<void>;
  listForProject: (projectId: string) => Promise<{
    items: ApiElevatedSession[];
    active: ApiElevatedSession | null;
    allowConsoleSim: boolean;
  }>;
  request: (input: {
    projectId: string;
    reason: string;
    scope: "维护" | "配置";
    durationMinutes: 30 | 120 | 480;
  }) => Promise<{
    session: ApiElevatedSession;
    autoApproved?: boolean;
    ownerLink?: string;
    notify?: ApiElevatedNotify;
  }>;
  approveSim: (sessionId: string) => Promise<ApiElevatedSession>;
  resendNotify: (sessionId: string) => Promise<{
    session: ApiElevatedSession;
    ownerLink?: string;
    notify?: ApiElevatedNotify;
  }>;
  revoke: (sessionId: string, reason?: string) => Promise<ApiElevatedSession>;
}

const ElevatedContext = createContext<ElevatedState | null>(null);

export function ElevatedSessionsProvider({ children }: { children: ReactNode }) {
  const { email, isAuthenticated } = useAuth();
  const [cloudReady, setCloudReady] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [active, setActive] = useState<ApiElevatedSession[]>([]);
  const [all, setAll] = useState<ApiElevatedSession[]>([]);
  const [allowConsoleSim, setAllowConsoleSim] = useState(false);

  const refresh = useCallback(async () => {
    setSyncing(true);
    try {
      const ok = await cloudHealth();
      if (!ok) {
        setCloudReady(false);
        setActive([]);
        return;
      }
      if (!getCloudToken() && email) {
        try {
          const sess = await ensureCloudSession(email);
          if (sess.ok === false) throw new Error(sess.message);
        } catch {
          setCloudReady(false);
          return;
        }
      }
      if (!getCloudToken()) {
        setCloudReady(false);
        return;
      }
      const [{ items: activeItems }, { items: allItems }] = await Promise.all([
        cloudListElevatedSessions({ activeOnly: true }),
        cloudListElevatedSessions({}),
      ]);
      setActive(activeItems);
      setAll(allItems);
      const flag = allItems.some((s) => s.allowConsoleSim === true);
      if (flag) setAllowConsoleSim(true);
      setCloudReady(true);
    } catch {
      setCloudReady(false);
    } finally {
      setSyncing(false);
    }
  }, [email]);

  useEffect(() => {
    if (!isAuthenticated) {
      setActive([]);
      return;
    }
    void refresh();
    const t = window.setInterval(() => void refresh(), 15_000);
    return () => window.clearInterval(t);
  }, [isAuthenticated, refresh]);

  const listForProject = useCallback(async (projectId: string) => {
    const data = await cloudListProjectElevatedSessions(projectId);
    if (typeof data.allowConsoleSim === "boolean") {
      setAllowConsoleSim(data.allowConsoleSim);
    }
    return {
      items: data.items,
      active: data.active,
      allowConsoleSim: Boolean(data.allowConsoleSim),
    };
  }, []);

  const request = useCallback(
    async (input: {
      projectId: string;
      reason: string;
      scope: "维护" | "配置";
      durationMinutes: 30 | 120 | 480;
    }) => {
      const data = await cloudRequestElevatedSession(input.projectId, {
        reason: input.reason,
        scope: input.scope,
        durationMinutes: input.durationMinutes,
        requester: email || undefined,
      });
      await refresh();
      return {
        session: data.session,
        autoApproved: data.autoApproved,
        ownerLink: data.ownerLink || data.session.ownerLink,
        notify: data.notify || data.session.notify,
      };
    },
    [email, refresh],
  );

  const approveSim = useCallback(
    async (sessionId: string) => {
      const { session } = await cloudApproveElevatedSession(sessionId, {
        channel: "console_sim",
        approver: "业主（控制台模拟）",
      });
      await refresh();
      return session;
    },
    [refresh],
  );

  const resendNotify = useCallback(
    async (sessionId: string) => {
      const data = await cloudResendElevatedNotify(sessionId);
      await refresh();
      return {
        session: data.session,
        ownerLink: data.ownerLink || data.session.ownerLink,
        notify: data.notify || data.session.notify,
      };
    },
    [refresh],
  );

  const revoke = useCallback(
    async (sessionId: string, reason?: string) => {
      const { session } = await cloudRevokeElevatedSession(sessionId, {
        reason,
        actor: email || undefined,
      });
      await refresh();
      return session;
    },
    [email, refresh],
  );

  const value = useMemo<ElevatedState>(
    () => ({
      cloudReady,
      active,
      all,
      syncing,
      allowConsoleSim,
      refresh,
      listForProject,
      request,
      approveSim,
      resendNotify,
      revoke,
    }),
    [
      cloudReady,
      active,
      all,
      syncing,
      allowConsoleSim,
      refresh,
      listForProject,
      request,
      approveSim,
      resendNotify,
      revoke,
    ],
  );

  return (
    <ElevatedContext.Provider value={value}>{children}</ElevatedContext.Provider>
  );
}

export function useElevatedSessions() {
  const ctx = useContext(ElevatedContext);
  if (!ctx) {
    throw new Error("useElevatedSessions must be used within ElevatedSessionsProvider");
  }
  return ctx;
}
